import { createContext, useContext } from "react";
import type { BreakdownLinkAccess } from "./useBreakdownLinkAccess";

/**
 * DAT-906 · What any piece of one open wage breakdown needs to know about the breakdown as a whole, without
 * threading it through every segment and line: which shift it is (so its links can offer the way back),
 * and what its hover cards may load and show.
 */
export type BreakdownPanel = {
	/** The nurse-in-shift id whose row the breakdown hangs under. */
	shiftId: string;
	isActual: boolean;
	/** Whether the rule lines are unfolded now: a link out carries it, so the way back reopens them the same way. */
	detailsOpen: boolean;
	/**
	 * An agency shift: its segments' cards include the agency's row from Agency Configuration, for a viewer
	 * who may open that tab (`access.canOpenAgencyConfiguration`).
	 */
	isAgency: boolean;
	agencyName: string | null;
	/** The panel's hide rule: amounts show only with wages revealed. */
	canViewWage: boolean;
	access: BreakdownLinkAccess;
};

const BreakdownPanelContext = createContext<BreakdownPanel | null>(null);

export const BreakdownPanelProvider = BreakdownPanelContext.Provider;

/** The open breakdown. Only its own pieces call this; outside a breakdown it is a bug, so it throws. */
export const useBreakdownPanel = (): BreakdownPanel => {
	const panel = useContext(BreakdownPanelContext);
	if (panel === null) throw new Error("useBreakdownPanel must be used inside a wage breakdown (BreakdownPanelProvider)");
	return panel;
};
